import type { PropsWithChildren } from "react";
import type { RegistryItemPreviewResult } from "schemas/generated/registry";
import type { WorkspaceDefaultProjectType } from "schemas/generated/workspaceDefaultProjectType";

import * as React from "react";

import AuthGradientBackground from "@svg/auth-gradient-background";

import { BedrockClient } from "@replohq/bedrock-sdk";
import { BedrockProvider } from "@replohq/bedrock-sdk/provider";
import { useBedrock } from "@replohq/bedrock-sdk/react";
import { ReploLogoGlyph } from "@replohq/lattice/blocks/brand/ReploLogo";
import { clearArrivalWorkflow } from "@replohq/lattice/blocks/workflow-checklist/arrivalWorkflow";
import { Button } from "@replohq/lattice/components/button/Button";
import { PortalThemeProvider } from "@replohq/lattice/components/portal-theme/PortalTheme";
import {
  getPendingRegistryInstallItemId,
  savePendingRegistryInstallItemId,
} from "@replohq/lattice/components/registry-item-preview/pendingRegistryInstall";
import { RegistryItemPreview } from "@replohq/lattice/components/registry-item-preview/RegistryItemPreview";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import { skipToken, useQuery } from "@tanstack/react-query";
import {
  Screen,
  ScreenData,
  useNextUrlWithPendingAppInstallationId,
} from "@v2/features/account/common";
import { ProductShowcasePanel } from "@v2/features/account/product-showcase-panel/ProductShowcasePanel";
import { analytics } from "@v2/features/infra/analytics/tracking";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useTheme } from "@v2/hooks/useTheme";
import { fileTemplateRouter } from "@v2/trpc/routes/fileTemplate";
import { getBedrockApiUrl, getCoordinatorApiUrl } from "@v2/utils/env";
import { readFreshMarketingSeedAndPurgeStale } from "@v2/utils/marketing-seed";
import { routes } from "@v2/utils/router";
import { UserRound } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import {
  matchPath,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import useMeasure from "react-use-measure";
import { AGENT_DOCUMENT_ROUTE_HANDOFF_STORAGE_KEY } from "replo-utils/lib/agentApplicationPaths";
import { findChatSignInNext } from "schemas/actionLinkRoutes";

import {
  getMarketingWorkflow,
  getMarketingWorkflowLandingPath,
} from "./marketingWorkflow";
import {
  getCampaignSourceFromParams,
  hasShopifyInstallIntent,
} from "./signupFunnelUtils";
import { TermsAndPrivacy } from "./TermsAndPrivacy";
import {
  WorkflowAuthFormHeading,
  WorkflowAuthPreview,
} from "./WorkflowAuthIntro";

type PendingRegistryInstallPreviewItem = RegistryItemPreviewResult["item"];
const pendingRegistryInstallPreviewDesktopWidth = 1440;

function PublicRegistryBedrockProvider({ children }: PropsWithChildren) {
  const client = React.useMemo(() => {
    return new BedrockClient({
      appId: "registry-preview",
      bedrockApiUrl: getBedrockApiUrl(),
      agentCoordinatorBaseUrl: getCoordinatorApiUrl(),
      auth: { type: "public" },
    });
  }, []);

  return <BedrockProvider client={client}>{children}</BedrockProvider>;
}

const pendingRegistryInstallTypeLabelByType: Record<
  PendingRegistryInstallPreviewItem["type"],
  string
> = {
  site: "Shared site",
  page: "Shared page",
  section: "Shared section",
  component: "Shared component",
  skill: "Shared skill",
  branding: "Shared brand kit",
  plan: "Shared plan",
  product: "Shared product",
  reference: "Shared reference",
  canvas: "Shared Canvas template",
  "inspiration-section": "Shared inspiration section",
  "curated-stage": "Shared curated stages",
};

export function AuthLayout() {
  const shouldReduceMotion = useReducedMotion();
  const { effectiveMode, palette, cssVars } = useTheme();
  const systemThemeCss = {
    ...cssVars,
    "--color-component-border":
      "color-mix(in srgb, var(--color-placeholder) 80%, var(--color-sheet))",
  };
  const surfaceAttributes = {
    "data-agent-theme": effectiveMode,
    "data-replo-surface": "agent-ui",
    "data-public-theme": "",
  };
  const surfaceStyle = {
    ...systemThemeCss,
    colorScheme: effectiveMode,
  };
  const { isAuthenticated, user } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const authScreen = Object.keys(ScreenData).find((path) => {
    return matchPath(path, location.pathname);
  });
  const [searchParams] = useSearchParams();
  const marketingWorkflow = getMarketingWorkflow(searchParams);
  const { nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  const pageTemplateId = searchParams.get("pageTemplateId") ?? null;
  if (pageTemplateId) {
    sessionStorage.setItem("replo_pending_template_id", pageTemplateId);
  }
  // Share pages route anonymous visitors here through the item owner's
  // affiliate link (cross-origin), so the pending install arrives as a query
  // param instead of sessionStorage.
  const registryItemIdParam = searchParams.get("registryItemId") ?? null;
  if (registryItemIdParam) {
    savePendingRegistryInstallItemId({ itemId: registryItemIdParam });
  }
  const pendingRegistryInstallItemId =
    registryItemIdParam ?? getPendingRegistryInstallItemId();

  const isSignup = authScreen === Screen.SIGNUP;
  const authRoute: "signup" | "login" = isSignup ? "signup" : "login";
  const campaignSource = getCampaignSourceFromParams(searchParams);
  const hasTrackedInitiatedRef = React.useRef(false);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (pageTemplateId && !hasTrackedInitiatedRef.current) {
      hasTrackedInitiatedRef.current = true;
      analytics.logEvent("sharedPageTemplate.auth.initiated", {
        templateId: pageTemplateId,
        authRoute,
      });
    }
  }, [pageTemplateId, authRoute]);

  const marketingSiteSeedData = readFreshMarketingSeedAndPurgeStale();
  const marketingSitePrompt = marketingSiteSeedData
    ? marketingSiteSeedData.prompt
    : null;
  const marketingSiteImage =
    marketingSiteSeedData?.hasImage && marketingSiteSeedData.jti
      ? `https://marketing-uploads.replocdn.com/marketing-website-prompt-uploads/${marketingSiteSeedData.jti}`
      : "";

  const [userSelectedPath, setDefaultProjectType] =
    React.useState<WorkspaceDefaultProjectType | null>(null);
  const isShopifyInstallIntent = hasShopifyInstallIntent(searchParams);
  let defaultProjectType: WorkspaceDefaultProjectType;
  if (marketingWorkflow) {
    defaultProjectType = "agent";
  } else if (isShopifyInstallIntent) {
    defaultProjectType = "shopify_theme_builder";
  } else {
    defaultProjectType = userSelectedPath ?? "agent";
  }

  const isPasswordResetRoute = location.pathname.startsWith(
    "/auth/password/reset",
  );
  const nextSearchString = nextSearchParams.toString();
  const externalNextUrl =
    nextUrl && /^https?:\/\//.test(nextUrl)
      ? `${nextUrl}${nextSearchString ? `?${nextSearchString}` : ""}`
      : null;
  const redirectToExternalNext = React.useCallback(
    (element: HTMLElement | null) => {
      if (element && externalNextUrl) {
        window.location.replace(externalNextUrl);
      }
    },
    [externalNextUrl],
  );
  if (isAuthenticated && !isPasswordResetRoute) {
    if (externalNextUrl) {
      return <main ref={redirectToExternalNext}>Redirecting…</main>;
    }
    const workflowLandingPath = getMarketingWorkflowLandingPath({
      searchParams,
      agentProjectId: user?.defaultAgentProjectId,
    });
    if (workflowLandingPath) {
      // Opened here, so a later onboarding in this browser is not told to start it again.
      clearArrivalWorkflow();
      return <Navigate to={workflowLandingPath} replace />;
    }
    return (
      <Navigate
        to={{
          pathname: nextUrl ?? routes.home.root,
          search: nextSearchString,
        }}
        replace
      />
    );
  }

  // `prompt=none` asks only to renew an existing session (artifacts.replo.app does this when its session cookie lapses), so a signed-out visitor goes straight back.
  if (searchParams.get("prompt") === "none" && externalNextUrl) {
    return <main ref={redirectToExternalNext}>Redirecting…</main>;
  }

  // A signed-out visitor reaching this screen means the Agent app's bounce landed; left set, its next bounce in this tab reads as a loop and shows "Page not found".
  sessionStorage.removeItem(AGENT_DOCUMENT_ROUTE_HANDOFF_STORAGE_KEY);

  const renderData = authScreen ? ScreenData[authScreen] : undefined;
  if (marketingWorkflow && !isPasswordResetRoute) {
    return (
      <PortalThemeProvider
        style={systemThemeCss}
        attributes={surfaceAttributes}
      >
        <div
          className="grid min-h-dvh bg-home-canvas font-sans text-default lg:grid-cols-2"
          {...surfaceAttributes}
          style={surfaceStyle}
        >
          <style>{`html, body { background-color: ${palette.homeCanvas} !important; }`}</style>
          <aside className="order-2 m-4 hidden self-center overflow-hidden rounded-3xl bg-sheet lg:block">
            <WorkflowAuthPreview {...marketingWorkflow} />
          </aside>
          <main className="order-1 flex min-h-dvh min-w-0 px-6 py-8 lg:px-12">
            {/* Auto margins centre the form and still let a short window scroll to its top. */}
            <div className="m-auto w-full min-w-0 max-w-sm">
              <ReploLogoGlyph className="mb-8 size-8 text-default" />
              <WorkflowAuthFormHeading
                workflowTitle={marketingWorkflow.workflow.title}
                authRoute={authRoute}
              />
              <Outlet
                context={{
                  defaultProjectType: "agent",
                  setDefaultProjectType,
                }}
              />
              <Button
                variant="tertiary"
                size="lg"
                className="mt-4 h-auto min-h-11 w-full"
                UNSAFE_className="py-2 whitespace-normal text-balance text-sm text-default hover:text-default"
                onClick={() => {
                  return navigate({
                    pathname: isSignup ? Screen.LOGIN : Screen.SIGNUP,
                    search: searchParams.toString(),
                  });
                }}
              >
                {isSignup ? (
                  <>
                    <span className="text-muted">Already have an account?</span>{" "}
                    <span className="text-default">Sign in to continue</span>
                  </>
                ) : (
                  "Create an account"
                )}
              </Button>
            </div>
          </main>
        </div>
      </PortalThemeProvider>
    );
  }

  // Links and previews that pick a path for the visitor (an App Store install, a workflow, a template, a chat's action link) leave nothing to switch.
  const canSwitchToThemeBuilder =
    isSignup &&
    !findChatSignInNext(searchParams.get("next")) &&
    defaultProjectType === "agent" &&
    !isShopifyInstallIntent &&
    !marketingWorkflow &&
    !pageTemplateId &&
    !pendingRegistryInstallItemId &&
    !(marketingSiteSeedData && marketingSitePrompt);

  const authFormContent = (
    <div className="flex w-full flex-col gap-4">
      {(isSignup || authScreen === Screen.LOGIN) && (
        <ReploLogoGlyph className="mx-auto size-10 text-default" />
      )}
      <Outlet
        context={{
          defaultProjectType,
          setDefaultProjectType,
        }}
      />
      <div className="text-center text-sm text-default">
        <button
          type="button"
          className="cursor-pointer min-h-11 w-full rounded-lg px-2 py-2 text-center text-sm text-default hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            const query = getQueryDefaultsFromLocation(searchParams);
            if (renderData?.footerCtaRedirectTo) {
              // Note (Sebas, 2023-06-30): This is the place where we set the next
              // parameter for the login/signup flow to prevent losing the value.
              navigate(
                // Note (Noah, 2024-02-14): Preserve query strings in this navigate call, since
                // we have query params like shopifyShopURL and pendingInstallationId we need to
                // preserve in order to make the claim flow work if they just came from installing
                // the shopify integration
                {
                  pathname: renderData.footerCtaRedirectTo,
                  search: searchParams.toString(),
                  hash: location.hash,
                },
                {
                  state: {
                    next: query.next ?? null,
                  },
                },
              );
            }
          }}
        >
          {renderData?.footerText} {renderData?.footerCtaText}
        </button>
      </div>
      {isSignup && (
        <div className="mx-auto max-w-xs text-center opacity-80 [&>div]:font-normal [&_a]:font-normal">
          <TermsAndPrivacy subdued />
        </div>
      )}
      {canSwitchToThemeBuilder && (
        <p className="text-center text-sm text-muted">
          Looking for{" "}
          <button
            type="button"
            className="cursor-pointer text-default underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setDefaultProjectType("shopify_theme_builder")}
          >
            Shopify Theme Builder
          </button>
          ?
        </p>
      )}
    </div>
  );

  const authPreview = (
    <AuthPageLeftPanel
      pageTemplateId={pageTemplateId}
      pendingRegistryInstallItemId={pendingRegistryInstallItemId}
      isSignup={isSignup}
      hasMarketingSiteSeedData={Boolean(marketingSiteSeedData)}
      marketingSitePrompt={marketingSitePrompt}
      marketingSiteImage={marketingSiteImage}
      defaultProjectType={defaultProjectType}
      campaignSource={campaignSource}
    />
  );

  return (
    <PortalThemeProvider style={systemThemeCss} attributes={surfaceAttributes}>
      <div
        className="no-scrollbar flex min-h-dvh w-full flex-col items-center justify-center gap-0 overflow-x-hidden overflow-y-auto bg-home-canvas px-4 font-sans text-default md:py-10 lg:h-dvh lg:w-screen lg:flex-row lg:gap-0 lg:px-0 lg:py-0"
        {...surfaceAttributes}
        style={surfaceStyle}
      >
        <style>{`html, body { background-color: ${palette.homeCanvas} !important; }`}</style>
        <div className="order-2 flex h-full w-full max-w-[400px] flex-col items-center justify-center lg:w-1/2 lg:max-w-none lg:items-stretch lg:p-0">
          {authPreview}
        </div>
        <div
          className="order-1 my-4 flex w-full items-center justify-center lg:my-0 lg:w-1/2"
          data-auth-form
        >
          <motion.div
            layout={shouldReduceMotion ? false : "position"}
            transition={{
              duration: DURATION.standard,
              ease: EASING.inOutQuart,
            }}
            className="mx-auto w-full max-w-sm md:mx-0"
          >
            {authFormContent}
          </motion.div>
        </div>
      </div>
    </PortalThemeProvider>
  );
}

function AuthPageLeftPanel({
  pageTemplateId,
  pendingRegistryInstallItemId,
  isSignup,
  hasMarketingSiteSeedData,
  marketingSitePrompt,
  marketingSiteImage,
  defaultProjectType,
  campaignSource,
}: {
  pageTemplateId: string | null;
  pendingRegistryInstallItemId: string | null;
  isSignup: boolean;
  hasMarketingSiteSeedData: boolean;
  marketingSitePrompt: string | null;
  marketingSiteImage: string;
  defaultProjectType: WorkspaceDefaultProjectType;
  campaignSource: string | null;
}) {
  if (pageTemplateId) {
    return (
      <TemplatePreview
        pageTemplateId={pageTemplateId}
        isSignup={isSignup}
        defaultProjectType={defaultProjectType}
      />
    );
  }

  if (pendingRegistryInstallItemId) {
    return (
      <PublicRegistryBedrockProvider>
        <PendingRegistryInstallPreviewPanel
          itemId={pendingRegistryInstallItemId}
          isSignup={isSignup}
          defaultProjectType={defaultProjectType}
        />
      </PublicRegistryBedrockProvider>
    );
  }

  if (hasMarketingSiteSeedData && marketingSitePrompt) {
    return (
      <ReploChatPreview
        marketingSitePrompt={marketingSitePrompt}
        marketingSiteImage={marketingSiteImage}
      />
    );
  }

  return (
    <div className="hidden size-full p-4 lg:block">
      <ProductShowcasePanel
        defaultProjectType={defaultProjectType}
        campaignSource={campaignSource}
      />
    </div>
  );
}

function PendingRegistryInstallPreviewPanel({
  itemId,
  isSignup,
  defaultProjectType,
}: {
  itemId: string;
  isSignup: boolean;
  defaultProjectType: WorkspaceDefaultProjectType;
}) {
  const { client } = useBedrock();
  const shouldReduceMotion = useReducedMotion();
  const { data, isError, isLoading } = useQuery({
    queryKey: ["pending-registry-install-preview", itemId],
    queryFn: () => client.registry.items.getPreview({ id: itemId }),
    retry: false,
  });
  const item = data?.item ?? null;
  const itemTitle = item?.name ?? "Shared item";
  const itemTypeLabel = item
    ? pendingRegistryInstallTypeLabelByType[item.type]
    : "Shared item";
  const ctaText = isSignup
    ? "Sign up to add this shared item to your workspace."
    : "Log in to add this shared item to your workspace.";

  if (isError || (!isLoading && !item)) {
    return <ProductShowcasePanel defaultProjectType={defaultProjectType} />;
  }

  return (
    <div className="relative hidden h-full w-full overflow-hidden bg-black lg:block">
      <div className="absolute inset-0 flex items-end justify-center overflow-hidden">
        <AuthGradientBackground className="absolute bottom-0 h-auto min-h-full w-full" />
      </div>
      <motion.div
        className="relative z-10 flex h-full flex-col items-center justify-end overflow-hidden"
        initial={
          shouldReduceMotion ? false : { opacity: 0, y: 24, scale: 0.98 }
        }
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : DURATION.slow,
          ease: EASING.outQuint,
        }}
      >
        <div className="flex shrink-0 flex-col items-center gap-2 px-12 text-center text-default">
          <div className="text-sm font-medium text-muted">{itemTypeLabel}</div>
          <h2 className="line-clamp-2 font-editorial text-4xl leading-tight">
            {itemTitle}
          </h2>
          <p className="max-w-md text-sm leading-6 text-muted">{ctaText}</p>
        </div>
        <div className="flex w-full justify-center px-8 pt-10">
          <div className="-mb-20 h-[calc(100vh-220px)] min-h-[420px] w-[min(45vw,900px)] overflow-hidden rounded-t-lg bg-sheet shadow-2xl ring-1 ring-border">
            <RegistryItemPreview
              item={item}
              isLoading={isLoading}
              isError={false}
              className="h-full w-full"
              htmlViewportWidth={pendingRegistryInstallPreviewDesktopWidth}
              disableHtmlScroll
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function TemplatePreview({
  pageTemplateId,
  isSignup,
  defaultProjectType,
}: {
  pageTemplateId: string;
  isSignup: boolean;
  defaultProjectType: WorkspaceDefaultProjectType;
}) {
  const { data: template, isLoading } = fileTemplateRouter.getPublic.useQuery(
    pageTemplateId ? { id: pageTemplateId } : skipToken,
  );

  const [containerRef, containerDimensions] = useMeasure();

  const desktopWidth = 1440;
  const scale = React.useMemo(() => {
    if (containerDimensions.width > 0) {
      return containerDimensions.width / desktopWidth;
    }
    return 0.5; // Default scale
  }, [containerDimensions.width]);

  const ctaText = isSignup
    ? "Sign up to start building."
    : "Log in to start building.";

  if (isLoading) {
    return (
      <div className="hidden h-full w-full overflow-hidden bg-black lg:flex lg:items-center lg:justify-center">
        <Skeleton className="w-3/4 h-3/4" />
      </div>
    );
  }

  if (!template || !template.staticHtml) {
    return <ProductShowcasePanel defaultProjectType={defaultProjectType} />;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap');

        @font-face {
          font-family: 'PP Editorial New';
          src: url('https://assets.replocdn.com/PPEditorialNew-Regular.woff2') format('woff2');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'PP Editorial New';
          src: url('https://assets.replocdn.com/PPEditorialNew-Italic.woff2') format('woff2');
          font-weight: normal;
          font-style: italic;
          font-display: swap;
        }

      `}</style>
      <div className="hidden h-full w-full overflow-hidden bg-black relative lg:block">
        <div className="absolute inset-0 flex items-end justify-center overflow-hidden">
          <AuthGradientBackground className="absolute bottom-0 w-full h-auto min-h-full" />
        </div>

        {/* Content + Preview Container */}
        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-0">
          {/* Content */}
          <div className="flex flex-col items-center px-12 pb-12">
            <div className="text-4xl text-white font-editorial text-center mb-2">
              {template.name ?? "Untitled Template"}
            </div>
            <div className="text-base text-white/80 font-light text-center">
              {ctaText}
            </div>
          </div>

          {/* Template preview - sticks up from bottom and extends beyond */}
          <div className="w-full flex justify-center px-8 pt-12">
            <div
              ref={containerRef}
              className="overflow-hidden rounded-t-lg shadow-2xl -mb-20 w-[min(45vw,900px)] h-[calc(100vh-220px)]"
            >
              <div
                className="origin-top-left overflow-y-auto rounded-t-lg bg-white w-[1440px] no-scrollbar"
                style={{
                  height: `${100 / scale}%`,
                  transform: `scale(${scale})`,
                }}
              >
                <iframe
                  srcDoc={template.staticHtml}
                  className="w-full h-full bg-white"
                  sandbox="allow-scripts allow-same-origin"
                  title="Template Preview"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ReploChatPreview({
  marketingSitePrompt,
  marketingSiteImage,
}: {
  marketingSitePrompt: string;
  marketingSiteImage: string;
}) {
  return (
    <div className="m-4 hidden h-[calc(100%-2rem)] overflow-hidden rounded-3xl bg-sheet px-25 py-30 lg:block">
      <div
        className={cn(
          "flex flex-col items-center gap-6",
          marketingSiteImage ? "pt-20" : "pt-30",
        )}
      >
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 bg-[url('/images/onboarding/replo-logo-green-background.svg')] bg-cover bg-center flex-shrink-0" />
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="text-default text-base font-light">Replo AI</div>
              <div className="border border-border bg-surface text-default text-base font-light rounded-lg p-2 w-full">
                What can I help you build?
              </div>
            </div>
            <div className="w-10 h-10 flex-shrink-0"></div>
          </div>
        </div>
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 flex-shrink-0"></div>
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="flex items-end justify-between">
                <div className="text-default text-base font-light">You</div>
                {marketingSiteImage && (
                  <div className="w-20 h-20">
                    <img
                      src={marketingSiteImage}
                      alt="Marketing site reference"
                      className="w-full h-full object-cover rounded-lg border border-border"
                    />
                  </div>
                )}
              </div>
              <div className="border border-border bg-hover text-default text-base font-light rounded-lg p-2 w-full">
                {marketingSitePrompt}
              </div>
            </div>
            <div className="w-10 h-10 bg-surface flex items-center justify-center flex-shrink-0 border border-border rounded-lg">
              <UserRound className="w-5 h-5 text-default" />
            </div>
          </div>
        </div>
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 bg-[url('/images/onboarding/replo-logo-green-background.svg')] bg-cover bg-center flex-shrink-0" />
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="text-default text-base font-light">Replo AI</div>
              <div className="border border-border bg-surface text-default text-base font-light rounded-lg p-2 w-full">
                Great idea! Please sign up to start building.
              </div>
            </div>
            <div className="w-10 h-10 flex-shrink-0"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * We persist these between SIGNUP/LOGIN routes so that when someone installs the app
 * but already has an account, we can link the shopify URL to the correct account.
 */
function getQueryDefaultsFromLocation(searchParams: URLSearchParams) {
  const query: Record<string, string> = {};
  for (const key of [
    "email",
    "first_name",
    "last_name",
    "shopify_shop_url",
    "nonce",
    "next",
  ]) {
    const value = searchParams.get(key);
    if (value) {
      query[key] = value;
    }
  }
  return query;
}
